import React, { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { getConfig, patchConfig } from 'utils/appConfig';
import { institucionService } from 'services/institucionService';
import { InstitucionData } from 'types/institucion.types';
import { anioEscolarService } from 'services/usuarioService';
import { AñoEscolar } from 'types/usuario.types';

// ============================================================
// ConfiguracionPage — Fase 1: Institución + Año Escolar Activo
// Acceso: ADMIN (edición completa) y DIRECTOR (solo lectura en
// campos de institución, puede cambiar el año activo)
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
    <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
  </svg>
);

// ── Campo de formulario reutilizable ─────────────────────────
const Field: React.FC<{
  label:       string;
  value:       string;
  onChange?:   (v: string) => void;
  placeholder?: string;
  hint?:       string;
  readOnly?:   boolean;
  prefix?:     string;
}> = ({ label, value, onChange, placeholder, hint, readOnly, prefix }) => (
  <div>
    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
      {label}
    </label>
    <div className={`flex rounded-lg border overflow-hidden
      ${readOnly ? 'border-slate-100 bg-slate-50' : 'border-slate-200 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent'}`}>
      {prefix && (
        <span className="px-3 py-2.5 bg-slate-100 text-slate-500 text-sm border-r border-slate-200 flex-shrink-0">
          {prefix}
        </span>
      )}
      <input
        type="text"
        value={value}
        onChange={e => onChange?.(e.target.value)}
        placeholder={readOnly ? '—' : placeholder}
        readOnly={readOnly}
        className={`flex-1 px-3 py-2.5 text-sm outline-none bg-transparent
          ${readOnly ? 'text-slate-500 cursor-default' : 'text-slate-800 placeholder-slate-300'}`}
      />
    </div>
    {hint && <p className="text-slate-400 text-xs mt-1">{hint}</p>}
  </div>
);

// ── Sección: Datos de la Institución ─────────────────────────
const SeccionInstitucion: React.FC<{ esAdmin: boolean }> = ({ esAdmin }) => {
  const cfg = getConfig();

  const [form, setForm] = useState<Partial<InstitucionData>>({
    nombre:         cfg.institucionNombre,
    codigoModular:  cfg.codigoModular  ?? '',
    ugel:           cfg.ugel           ?? '',
    dre:            cfg.dre            ?? '',
    directorNombre: cfg.directorNombre ?? '',
    direccion:      cfg.direccion      ?? '',
    telefono:       cfg.telefono       ?? '',
    email:          cfg.email          ?? '',
    correodominio:  cfg.correodominio,
    correoSoporte:  cfg.correoSoporte,
    turno:          cfg.turno          ?? '',
  });
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const set = (field: keyof InstitucionData) => (v: string) => {
    setForm(p => ({ ...p, [field]: v }));
    setSuccess(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaving(true); setSaveError(null);
    try {
      await institucionService.update(form as InstitucionData);
      patchConfig({
        institucionNombre: form.nombre         || cfg.institucionNombre,
        correodominio:     form.correodominio  || cfg.correodominio,
        correoSoporte:     form.correoSoporte  || cfg.correoSoporte,
        codigoModular:     form.codigoModular,
        ugel:              form.ugel,
        dre:               form.dre,
        directorNombre:    form.directorNombre,
        direccion:         form.direccion,
        telefono:          form.telefono,
        email:             form.email,
        turno:             form.turno,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-800">Datos de la Institución</h2>
          <p className="text-slate-400 text-xs mt-0.5">
            {esAdmin
              ? 'Los cambios se guardan directamente en la base de datos.'
              : 'Solo el Administrador puede modificar estos datos.'}
          </p>
        </div>
        {!esAdmin && (
          <span className="text-xs font-semibold bg-slate-100 text-slate-500 px-3 py-1 rounded-full">
            Solo lectura
          </span>
        )}
      </div>

      <div className="p-6 space-y-5">
        {/* Identificación */}
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Identificación</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre de la institución"
              value={form.nombre ?? ''}  onChange={set('nombre')}
              placeholder="Ej: I.E. Nuestra Señora del Carmen"  readOnly={!esAdmin} />
            <Field label="Código Modular"
              value={form.codigoModular ?? ''}  onChange={set('codigoModular')}
              placeholder="Ej: 0123456"  readOnly={!esAdmin} />
            <Field label="UGEL"
              value={form.ugel ?? ''}  onChange={set('ugel')}
              placeholder="Ej: UGEL 07 - San Borja"  readOnly={!esAdmin} />
            <Field label="DRE"
              value={form.dre ?? ''}  onChange={set('dre')}
              placeholder="Ej: DRE Lima Metropolitana"  readOnly={!esAdmin} />
            <Field label="Director(a)"
              value={form.directorNombre ?? ''}  onChange={set('directorNombre')}
              placeholder="Ej: Prof. María García López"  readOnly={!esAdmin} />
            <Field label="Turno"
              value={form.turno ?? ''}  onChange={set('turno')}
              placeholder="Ej: MAÑANA"  readOnly={!esAdmin} />
          </div>
        </div>

        <hr className="border-slate-100" />

        {/* Contacto */}
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Contacto</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Dirección física"
              value={form.direccion ?? ''}  onChange={set('direccion')}
              placeholder="Ej: Av. Los Álamos 123, Lima"  readOnly={!esAdmin} />
            <Field label="Teléfono"
              value={form.telefono ?? ''}  onChange={set('telefono')}
              placeholder="Ej: (01) 234-5678"  readOnly={!esAdmin} />
            <Field label="Correo institucional"
              value={form.email ?? ''}  onChange={set('email')}
              placeholder="info@colegio.edu.pe"  readOnly={!esAdmin} />
            <Field label="Correo de soporte técnico"
              value={form.correoSoporte ?? ''}  onChange={set('correoSoporte')}
              placeholder="soporte@colegio.edu.pe"  readOnly={!esAdmin} />
          </div>
        </div>

        <hr className="border-slate-100" />

        {/* Correo institucional */}
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Correo Institucional</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Dominio de correo"
              value={form.correodominio ?? ''}  onChange={set('correodominio')}
              placeholder="@colegio.edu.pe"
              hint="Se usa como sufijo al crear correos de usuarios."
              readOnly={!esAdmin} />
          </div>
        </div>

        {saveError && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">{saveError}</div>
        )}

        {esAdmin && (
          <div className="flex items-center justify-between gap-3 pt-2">
            <div>
              {success && (
                <span className="flex items-center gap-1.5 text-green-600 text-sm font-semibold">
                  <CheckIcon /> Guardado correctamente
                </span>
              )}
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-700 hover:bg-blue-800
                         text-white text-sm font-bold transition-colors shadow-sm disabled:opacity-60"
            >
              {saving ? <SpinnerIcon /> : <CheckIcon />}
              Guardar en BD
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Sección: Gestión de Años Escolares ───────────────────────

const FASE_LABEL: Record<string, string> = {
  REGULAR:      'Regular',
  RECUPERACION: 'Recuperación',
};

const inputCls = 'w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:bg-slate-50';

interface AnioForm {
  anio:        string;
  fase:        'REGULAR' | 'RECUPERACION';
  fechaInicio: string;
  fechaFin:    string;
  activo:      boolean;
}

const FORM_VACIO: AnioForm = { anio: '', fase: 'REGULAR', fechaInicio: '', fechaFin: '', activo: false };

const SeccionAnioActivo: React.FC = () => {
  const [lista,    setLista]    = useState<AñoEscolar[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  // null = cerrado, -1 = nuevo, id = editando ese año
  const [editId,   setEditId]   = useState<number | null>(null);
  const [form,     setForm]     = useState<AnioForm>(FORM_VACIO);
  const [saving,   setSaving]   = useState(false);
  const [saveErr,  setSaveErr]  = useState<string | null>(null);

  const cargar = () => {
    setLoading(true); setError(null);
    anioEscolarService.listarTodos()
      .then(setLista)
      .catch(e => setError(e instanceof Error ? e.message : 'Error al cargar años'))
      .finally(() => setLoading(false));
  };
  useEffect(cargar, []);

  const abrirNuevo = () => {
    setForm({ ...FORM_VACIO, activo: false });
    setEditId(-1); setSaveErr(null);
  };

  const abrirEditar = (a: AñoEscolar) => {
    setForm({
      anio:        String(a.anio),
      fase:        a.fase,
      fechaInicio: a.fechaInicio ?? '',
      fechaFin:    a.fechaFin   ?? '',
      activo:      a.activo,
    });
    setEditId(a.idAnioEscolar); setSaveErr(null);
  };

  const cancelar = () => { setEditId(null); setSaveErr(null); };

  const guardar = async () => {
    if (!form.anio.trim()) { setSaveErr('El año es requerido'); return; }
    setSaving(true); setSaveErr(null);
    try {
      const payload = {
        anio:        Number(form.anio),
        fase:        form.fase,
        fechaInicio: form.fechaInicio || null,
        fechaFin:    form.fechaFin   || null,
        activo:      form.activo,
      };
      if (editId === -1) {
        await anioEscolarService.crear(payload);
      } else {
        await anioEscolarService.actualizar(editId!, payload);
      }
      cancelar();
      cargar();
    } catch (e: unknown) {
      setSaveErr(e instanceof Error ? e.message : 'Error al guardar');
    } finally { setSaving(false); }
  };

  const set = (f: keyof AnioForm) => (v: string | boolean) =>
    setForm(p => ({ ...p, [f]: v }));

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-800">Años Escolares</h2>
          <p className="text-slate-400 text-xs mt-0.5">
            Gestiona los años y su fase (Regular / Recuperación). El año activo se pre-selecciona en Matrículas y Reportes.
          </p>
        </div>
        <button onClick={abrirNuevo}
          className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
                     text-white text-xs font-bold transition-colors shadow-sm">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
            <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z"/>
          </svg>
          Nuevo año
        </button>
      </div>

      {/* Formulario nuevo/editar */}
      {editId !== null && (
        <div className="px-6 py-5 bg-slate-50 border-b border-slate-200 space-y-4">
          <p className="text-sm font-bold text-slate-700">
            {editId === -1 ? 'Registrar nuevo año escolar' : `Editar año ${form.anio}`}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Año */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Año *</label>
              <input type="number" value={form.anio} onChange={e => set('anio')(e.target.value)}
                placeholder="2025" min={2000} max={2099}
                className={inputCls} disabled={editId !== -1} />
            </div>

            {/* Fase */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Fase *</label>
              <select value={form.fase} onChange={e => set('fase')(e.target.value as 'REGULAR' | 'RECUPERACION')}
                className={inputCls}>
                <option value="REGULAR">Regular</option>
                <option value="RECUPERACION">Recuperación</option>
              </select>
            </div>

            {/* Fecha inicio */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Fecha inicio</label>
              <input type="date" value={form.fechaInicio} onChange={e => set('fechaInicio')(e.target.value)}
                className={inputCls} />
            </div>

            {/* Fecha fin */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Fecha fin</label>
              <input type="date" value={form.fechaFin} onChange={e => set('fechaFin')(e.target.value)}
                className={inputCls} />
            </div>
          </div>

          {/* Activo toggle */}
          <label className="flex items-center gap-3 cursor-pointer w-fit">
            <button type="button" onClick={() => set('activo')(!form.activo)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                ${form.activo ? 'bg-blue-600' : 'bg-slate-300'}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
                ${form.activo ? 'translate-x-6' : 'translate-x-1'}`}/>
            </button>
            <span className="text-sm font-semibold text-slate-700">
              {form.activo ? 'Año activo (vigente)' : 'Año inactivo'}
            </span>
          </label>

          {form.fase === 'RECUPERACION' && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
              </svg>
              <p className="text-amber-700 text-xs font-medium">
                Fase de Recuperación — las matrículas creadas en este período quedarán marcadas como
                <strong> RECUPERACION</strong>. Aparecerán diferenciadas con badge ámbar en la lista de matrículas.
              </p>
            </div>
          )}

          {saveErr && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{saveErr}</p>
          )}

          <div className="flex gap-2">
            <button onClick={guardar} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
                         text-white text-sm font-bold transition-colors disabled:opacity-60">
              {saving ? <SpinnerIcon /> : <CheckIcon />}
              {editId === -1 ? 'Registrar' : 'Guardar cambios'}
            </button>
            <button onClick={cancelar}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold
                         text-slate-600 hover:bg-slate-100 transition-colors">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista de años */}
      <div className="divide-y divide-slate-100">
        {loading ? (
          <div className="flex justify-center py-10"><SpinnerIcon /></div>
        ) : error ? (
          <div className="text-red-500 text-sm text-center py-8">{error}</div>
        ) : lista.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm">Sin años registrados</div>
        ) : (
          lista.map(a => {
            const esEdicion = editId === a.idAnioEscolar;
            return (
              <div key={a.idAnioEscolar}
                className={`flex items-center gap-4 px-6 py-4 transition-colors
                  ${esEdicion ? 'bg-blue-50/40' : a.activo ? 'bg-green-50/30' : ''}`}>

                {/* Año */}
                <div className="w-16 text-center">
                  <span className={`text-2xl font-black ${a.activo ? 'text-blue-700' : 'text-slate-400'}`}>
                    {a.anio}
                  </span>
                </div>

                {/* Fase */}
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full
                  ${a.fase === 'RECUPERACION'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-blue-100 text-blue-700'}`}>
                  {FASE_LABEL[a.fase] ?? a.fase}
                </span>

                {/* Fechas */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500">
                    {a.fechaInicio
                      ? `${a.fechaInicio}${a.fechaFin ? ` → ${a.fechaFin}` : ''}`
                      : <span className="text-slate-300">Sin fechas definidas</span>
                    }
                  </p>
                </div>

                {/* Estado */}
                {a.activo && (
                  <span className="text-xs font-bold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">
                    Vigente
                  </span>
                )}

                {/* Editar */}
                <button onClick={() => abrirEditar(a)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-blue-50
                             transition-colors"
                  title="Editar este año escolar">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path d="M5.433 13.917l1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65Z"/>
                    <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z"/>
                  </svg>
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};


// ── Componente principal ─────────────────────────────────────
const ConfiguracionPage: React.FC = () => {
  const { user } = useAuth();
  const esAdmin = user?.rol === 'ADMIN';

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <div>
        <h1 className="text-xl font-bold text-slate-800">Configuración</h1>
        <p className="text-slate-400 text-sm mt-0.5">
          Datos de la institución y año escolar activo.
        </p>
      </div>

      <SeccionAnioActivo />
      <SeccionInstitucion esAdmin={esAdmin} />
    </div>
  );
};

export default ConfiguracionPage;
